import Head from 'next/head';
import Link from 'next/link';
import React from 'react';
import { useRouter } from 'next/router';
import PasswordInput from '@/components/ui/PasswordInput';
import { getPasswordPolicyIssues, passwordStrengthOk } from '@/utils/passwordPolicy';
import {
  detectBrowserLocale,
  detectBrowserTimezone,
  normalizeLocaleCode,
  pickBestLocale,
  useI18n,
} from '@/i18n';

type RoleOption = {
  role_key: string;
  label: string;
};

type MetaRolesResponse = {
  ok: true;
  roles: RoleOption[];
};

type SignupInvite = {
  invite_type: 'referral' | 'org';
  organization_id: number | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  company_name: string | null;
  role_key: string | null;
  referral_code: string | null;
};

type SignupInviteResponse =
  | { ok: true; invite: SignupInvite }
  | { ok: false; code: string; message: string };

type SignupResponse = {
  ok: boolean;
  code?: string;
  message?: string;
  pending?: boolean;
  error_id?: string;
};

interface ZxcvbnResult {
  score: number;
  feedback: {
    warning: string;
    suggestions: string[];
  };
}

type ZxcvbnFn = (password: string) => ZxcvbnResult;

const PASSWORD_COLORS = [
  'bg-red-500',
  'bg-orange-500',
  'bg-yellow-500',
  'bg-lime-500',
  'bg-green-600',
];

const TURNSTILE_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=__turnstileOnLoad';

const EULA_URL = 'https://umpireassist.com/legal/eula';

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
          action?: string;
        },
      ) => string;
      reset?: (widgetId?: string) => void;
      remove?: (widgetId?: string) => void;
    };
    __turnstileOnLoad?: () => void;
  }
}

function getMarketingQuery(routerQuery: Record<string, string | string[] | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(routerQuery)) {
    if (!key.startsWith('utm_')) continue;
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item != null) params.append(key, item);
      });
    } else if (value != null) {
      params.append(key, value);
    }
  }
  const query = params.toString();
  return query ? `?${query}` : '';
}

export default function SignupPage() {
  const { locale, setLocale, t } = useI18n();
  const router = useRouter();
  const marketingQuery = React.useMemo(() => getMarketingQuery(router.query), [router.query]);

  const [roles, setRoles] = React.useState<RoleOption[]>([]);
  const [rolesLoading, setRolesLoading] = React.useState(true);
  const [rolesError, setRolesError] = React.useState<string | null>(null);

  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [company, setCompany] = React.useState('');
  const [roleKey, setRoleKey] = React.useState('');
  const [acceptEula, setAcceptEula] = React.useState(false);
  const [accountType, setAccountType] = React.useState<'individual' | 'company'>('individual');
  const [referralCode, setReferralCode] = React.useState('');
  const [inviteToken, setInviteToken] = React.useState<string | null>(null);
  const [inviteInfo, setInviteInfo] = React.useState<SignupInvite | null>(null);
  const [inviteError, setInviteError] = React.useState<string | null>(null);
  const [preferredLocale, setPreferredLocale] = React.useState(() =>
    normalizeLocaleCode(locale),
  );
  const [availableLocales, setAvailableLocales] = React.useState<
    Array<{ code: string; label: string }>
  >([]);
  const [localesLoading, setLocalesLoading] = React.useState(true);
  const [localesError, setLocalesError] = React.useState<string | null>(null);
  const [localeSearch, setLocaleSearch] = React.useState('');
  const [localeSearchDebounced, setLocaleSearchDebounced] = React.useState('');
  const [catalogLocales, setCatalogLocales] = React.useState<
    Array<{ code: string; label: string }>
  >([]);
  const [catalogLoading, setCatalogLoading] = React.useState(false);
  const [catalogError, setCatalogError] = React.useState<string | null>(null);
  const [localeDropdownOpen, setLocaleDropdownOpen] = React.useState(false);
  const localePickerRef = React.useRef<HTMLDivElement | null>(null);
  const [timezone, setTimezone] = React.useState(() => detectBrowserTimezone());

  const [passwordScore, setPasswordScore] = React.useState<number | null>(null);
  const [passwordFeedback, setPasswordFeedback] = React.useState<string | null>(null);
  const zxcvbnRef = React.useRef<ZxcvbnFn | null>(null);
  const [zxcvbnReady, setZxcvbnReady] = React.useState(false);

  const [captchaToken, setCaptchaToken] = React.useState('');
  const captchaContainerRef = React.useRef<HTMLDivElement | null>(null);
  const captchaWidgetIdRef = React.useRef<string | null>(null);
  const [captchaReady, setCaptchaReady] = React.useState(false);
  const captchaSiteKey =
    process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || process.env.NEXT_PUBLIC_CAPTCHA_SITE_KEY || '';

  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [submitted, setSubmitted] = React.useState(false);

  const abortControllerRef = React.useRef<AbortController | null>(null);
  const inviteAbortRef = React.useRef<AbortController | null>(null);

  React.useEffect(() => {
    if (preferredLocale !== locale) {
      setPreferredLocale(locale);
    }
  }, [locale, preferredLocale]);

  const fetchInstalledLocales = React.useCallback(async () => {
    const res = await fetch('/api/i18n/locales', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!res.ok) {
      throw new Error(t('profile.language.loadFailedStatus', { status: res.status }));
    }
    const data = (await res.json()) as {
      ok?: boolean;
      locales?: Array<{ code: string; displayName?: string | null; nativeName?: string | null }>;
    };
    const normalized = (data?.locales || [])
      .map((entry) => {
        const normalizedCode = normalizeLocaleCode(entry.code);
        if (!normalizedCode) return null;
        return {
          code: normalizedCode,
          label: entry.nativeName || entry.displayName || normalizedCode,
        };
      })
      .filter(Boolean) as Array<{ code: string; label: string }>;
    const seen = new Set<string>();
    const deduped = normalized.filter((entry) => {
      if (seen.has(entry.code)) return false;
      seen.add(entry.code);
      return true;
    });
    return deduped.length
      ? deduped
      : [{ code: 'en', label: t('layout.language.english') }];
  }, [t]);

  const installLocale = React.useCallback(async (localeInput: string) => {
    try {
      const res = await fetch('/api/i18n/install-locale', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ locale: localeInput }),
      });
      const data = (await res.json().catch(() => null)) as {
        ok?: boolean;
        locale?: string;
      } | null;
      if (!res.ok || !data?.ok || !data.locale) {
        return null;
      }
      return normalizeLocaleCode(data.locale);
    } catch {
      return null;
    }
  }, []);

  React.useEffect(() => {
    if (typeof window === 'undefined') return () => {};
    let cancelled = false;

    const loadLocales = async () => {
      setLocalesLoading(true);
      setLocalesError(null);
      try {
        const stored = window.localStorage.getItem('ua_locale');
        const detected = stored || detectBrowserLocale() || 'en';
        const installedLocale = await installLocale(detected);
        const resolvedLocales = await fetchInstalledLocales();
        if (cancelled) return;
        setAvailableLocales(resolvedLocales);
        const resolved = installedLocale
          ? installedLocale
          : pickBestLocale(
              detected,
              resolvedLocales.map((entry) => entry.code),
              'en',
            );
        setPreferredLocale(resolved);
        setLocale(resolved);
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof Error ? err.message : t('profile.language.loadFailed');
        setLocalesError(message);
        const fallbackLocales = [{ code: 'en', label: t('layout.language.english') }];
        setAvailableLocales(fallbackLocales);
        const stored = window.localStorage.getItem('ua_locale');
        const preferred = stored || detectBrowserLocale();
        const resolved = pickBestLocale(
          preferred,
          fallbackLocales.map((entry) => entry.code),
          'en',
        );
        setPreferredLocale(resolved);
        setLocale(resolved);
      } finally {
        if (!cancelled) setLocalesLoading(false);
      }
    };

    loadLocales();

    return () => {
      cancelled = true;
    };
  }, [fetchInstalledLocales, installLocale, setLocale]);

  React.useEffect(() => {
    if (!localeDropdownOpen) return;
    const handleClick = (event: MouseEvent) => {
      if (!localePickerRef.current) return;
      if (!localePickerRef.current.contains(event.target as Node)) {
        setLocaleDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [localeDropdownOpen]);

  React.useEffect(() => {
    if (!localeDropdownOpen) {
      setLocaleSearch('');
    }
  }, [localeDropdownOpen]);

  React.useEffect(() => {
    const handle = window.setTimeout(() => {
      setLocaleSearchDebounced(localeSearch.trim());
    }, 200);
    return () => {
      window.clearTimeout(handle);
    };
  }, [localeSearch]);

  React.useEffect(() => {
    if (!localeSearchDebounced) {
      setCatalogLocales([]);
      setCatalogLoading(false);
      setCatalogError(null);
      return;
    }
    let active = true;
    setCatalogLoading(true);
    setCatalogError(null);

    const fetchCatalog = async () => {
      try {
        const res = await fetch(
          `/api/i18n/catalog?q=${encodeURIComponent(localeSearchDebounced)}`,
          {
            method: 'GET',
            headers: { Accept: 'application/json' },
            cache: 'no-store',
          },
        );
        if (!res.ok) {
          throw new Error(t('profile.language.loadFailedStatus', { status: res.status }));
        }
        const data = (await res.json()) as {
          ok?: boolean;
          locales?: Array<{ code: string; englishName?: string | null; nativeName?: string | null }>;
        };
        if (!active) return;
        const formatted = (data?.locales || [])
          .map((entry) => {
            const code = normalizeLocaleCode(entry.code);
            if (!code) return null;
            const english = entry.englishName ? String(entry.englishName) : '';
            const native = entry.nativeName ? String(entry.nativeName) : '';
            let label = native || english || code;
            if (native && english && native !== english) {
              label = t('signup.languageCatalogLabel', { native, english });
            }
            return { code, label };
          })
          .filter(Boolean) as Array<{ code: string; label: string }>;
        setCatalogLocales(formatted);
      } catch (err) {
        if (!active) return;
        setCatalogError(
          err instanceof Error ? err.message : t('profile.language.loadFailed'),
        );
      } finally {
        if (active) setCatalogLoading(false);
      }
    };

    fetchCatalog();

    return () => {
      active = false;
    };
  }, [localeSearchDebounced]);

  React.useEffect(() => {
    let mounted = true;
    if (typeof window === 'undefined') {
      return () => {
        mounted = false;
      };
    }

    import('zxcvbn')
      .then((module) => {
        if (!mounted) return;
        const fn = (module as unknown as { default?: ZxcvbnFn }).default || (module as unknown as ZxcvbnFn);
        zxcvbnRef.current = fn;
        setZxcvbnReady(true);
      })
      .catch(() => {
        if (mounted) {
          zxcvbnRef.current = null;
          setZxcvbnReady(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  React.useEffect(() => {
    let cancelled = false;

    async function fetchRoles() {
      setRolesLoading(true);
      setRolesError(null);
      try {
        const res = await fetch('/api/meta/roles', {
          method: 'GET',
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        if (!res.ok) {
          throw new Error(t('signup.loadRolesErrorStatus', { status: res.status }));
        }
        const data = (await res.json()) as MetaRolesResponse;
        if (!cancelled && data?.ok && Array.isArray(data.roles)) {
          setRoles(data.roles);
        }
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof Error
            ? err.message
            : t('signup.loadRolesError');
        setRolesError(message);
      } finally {
        if (!cancelled) {
          setRolesLoading(false);
        }
      }
    }

    fetchRoles();

    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (!router.isReady) return;
    const queryValue = (key: string): string | null => {
      const value = router.query[key];
      if (Array.isArray(value)) {
        return value[0] ?? null;
      }
      return value ?? null;
    };

    if (!firstName) {
      const value = queryValue('first_name');
      if (value) setFirstName(value);
    }
    if (!lastName) {
      const value = queryValue('last_name');
      if (value) setLastName(value);
    }
    if (!email) {
      const value = queryValue('email');
      if (value) setEmail(value);
    }
    if (!company) {
      const value = queryValue('company');
      if (value) setCompany(value);
    }
    if (!roleKey) {
      const value = queryValue('role_key');
      if (value) setRoleKey(value);
    }
    if (!referralCode) {
      const value = queryValue('referral_code') ?? queryValue('ref');
      if (value) setReferralCode(value);
    }
    if (accountType === 'individual') {
      const value = queryValue('account_type');
      if (value === 'company' || value === 'individual') {
        setAccountType(value);
      }
    }
    const inviteValue = queryValue('invite');
    if (inviteValue && inviteValue !== inviteToken) {
      setInviteToken(inviteValue);
    }
  }, [
    accountType,
    company,
    email,
    firstName,
    inviteToken,
    lastName,
    referralCode,
    roleKey,
    router.isReady,
    router.query,
  ]);

  React.useEffect(() => {
    if (!inviteToken) return () => {};
    inviteAbortRef.current?.abort();
    const controller = new AbortController();
    inviteAbortRef.current = controller;
    setInviteError(null);

    const fetchInvite = async () => {
      try {
        const res = await fetch(`/api/auth/signup-invite?token=${encodeURIComponent(inviteToken)}`, {
          method: 'GET',
          headers: { Accept: 'application/json' },
          cache: 'no-store',
          signal: controller.signal,
        });
        const data = (await res.json().catch(() => null)) as SignupInviteResponse | null;
        if (!res.ok || !data || !data.ok) {
          const message =
            data && 'message' in data ? data.message : t('signup.inviteLoadError');
          setInviteError(message);
          return;
        }
        const invite = data.invite;
        setInviteInfo(invite);
        if (invite.invite_type === 'org' && invite.organization_id) {
          setAccountType('company');
        }
        if (invite.first_name) setFirstName((prev) => prev || invite.first_name || '');
        if (invite.last_name) setLastName((prev) => prev || invite.last_name || '');
        if (invite.email) setEmail((prev) => prev || invite.email || '');
        if (invite.company_name) setCompany((prev) => prev || invite.company_name || '');
        if (invite.role_key) setRoleKey((prev) => prev || invite.role_key || '');
        if (invite.referral_code) setReferralCode((prev) => prev || invite.referral_code || '');
      } catch (err) {
        if ((err as { name?: string } | null)?.name === 'AbortError') return;
        setInviteError(t('signup.inviteLoadError'));
      }
    };

    fetchInvite();

    return () => {
      controller.abort();
    };
  }, [inviteToken]);

  React.useEffect(() => {
    if (!zxcvbnReady || !password || !zxcvbnRef.current) {
      setPasswordScore(password ? 0 : null);
      setPasswordFeedback(null);
      return;
    }
    try {
      const result = zxcvbnRef.current(password);
      setPasswordScore(result.score);
      const feedbackParts = [];
      if (result.feedback.warning) feedbackParts.push(result.feedback.warning);
      if (result.feedback.suggestions?.length) {
        feedbackParts.push(result.feedback.suggestions.join(' '));
      }
      setPasswordFeedback(feedbackParts.length ? feedbackParts.join(' ') : null);
    } catch {
      setPasswordScore(null);
      setPasswordFeedback(null);
    }
  }, [password, zxcvbnReady]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return () => {};

    if (!captchaSiteKey) {
      setCaptchaReady(true);
      setCaptchaToken('test');
      return () => {};
    }

    let cancelled = false;
    const container = captchaContainerRef.current;

    function renderWidget() {
      if (cancelled) return;
      if (captchaWidgetIdRef.current) return;
      if (!window.turnstile || !container) return;
      captchaWidgetIdRef.current = window.turnstile.render(container, {
        sitekey: captchaSiteKey,
        action: 'signup',
        callback: (token: string) => {
          if (!cancelled) {
            setCaptchaToken(token);
            setCaptchaReady(true);
          }
        },
        'expired-callback': () => {
          if (!cancelled) {
            setCaptchaToken('');
          }
        },
        'error-callback': () => {
          if (!cancelled) {
            setCaptchaToken('');
          }
        },
      });
    }

    function ensureScript() {
      const existingScript = document.querySelector<HTMLScriptElement>('script[data-turnstile]');
      if (existingScript) {
        if (window.turnstile) {
          renderWidget();
        }
        return;
      }
      const script = document.createElement('script');
      script.src = TURNSTILE_SRC;
      script.async = true;
      script.defer = true;
      script.setAttribute('data-turnstile', '');
      window.__turnstileOnLoad = () => {
        renderWidget();
      };
      document.head.appendChild(script);
    }

    ensureScript();

    return () => {
      cancelled = true;
      if (window.turnstile && captchaWidgetIdRef.current) {
        window.turnstile.remove?.(captchaWidgetIdRef.current);
        captchaWidgetIdRef.current = null;
      }
    };
  }, [captchaSiteKey]);

  React.useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      inviteAbortRef.current?.abort();
    };
  }, []);

  const onSubmit = React.useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (submitting) return;

      setError(null);
      setSubmitted(false);

      if (!acceptEula) {
        setError(t('signup.eulaRequired'));
        return;
      }

      if (accountType === 'company' && !company.trim()) {
        setError(t('signup.companyRequired'));
        return;
      }

      if (!confirmPassword) {
        setError(t('signup.confirmPasswordRequired'));
        return;
      }

      if (password !== confirmPassword) {
        setError(t('signup.passwordsMismatch'));
        return;
      }

      if (captchaSiteKey && !captchaToken) {
        setError(t('signup.captchaRequired'));
        return;
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      setSubmitting(true);

      try {
        const res = await fetch(`/api/auth/signup${marketingQuery}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            email: email.trim(),
            password,
            confirm_password: confirmPassword,
            company: accountType === 'company' ? company.trim() || undefined : undefined,
            role_key: roleKey,
            account_type: accountType,
            invite_token: inviteToken,
            referral_code: referralCode.trim() || undefined,
            acceptEula,
            captcha_token: captchaToken,
            preferred_locale: preferredLocale,
            timezone,
          }),
          signal: abortController.signal,
        });

        const data = (await res.json().catch(() => null)) as SignupResponse | null;
        if (!res.ok || !data?.ok) {
          const messageBase =
            data?.message ||
            (data?.code === 'WEAK_PASSWORD'
              ? t('signup.passwordWeak')
              : t('signup.submitError'));
          const message = data?.error_id
            ? `${messageBase} ${t('signup.supportCode', { code: data.error_id })}`
            : messageBase;
          setError(message);
          if (window.turnstile && captchaWidgetIdRef.current) {
            window.turnstile.reset?.(captchaWidgetIdRef.current);
          }
          return;
        }

        setSubmitted(true);
        setFirstName('');
        setLastName('');
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setCompany('');
        setRoleKey('');
        setAcceptEula(false);
        setAccountType('individual');
        setReferralCode('');
        setInviteToken(null);
        setInviteInfo(null);
        setCaptchaToken(captchaSiteKey ? '' : 'test');
        if (window.turnstile && captchaWidgetIdRef.current) {
          window.turnstile.reset?.(captchaWidgetIdRef.current);
        }
      } catch (err) {
        if ((err as { name?: string } | null)?.name === 'AbortError') {
          return;
        }
        const message =
          err instanceof Error ? err.message : t('signup.submitError');
        setError(message);
        if (window.turnstile && captchaWidgetIdRef.current) {
          window.turnstile.reset?.(captchaWidgetIdRef.current);
        }
      } finally {
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
        }
        setSubmitting(false);
      }
    },
    [
      acceptEula,
      captchaSiteKey,
      captchaToken,
      accountType,
      company,
      email,
      firstName,
      lastName,
      marketingQuery,
      password,
      confirmPassword,
      referralCode,
      roleKey,
      submitting,
      preferredLocale,
      timezone,
    ],
  );

  const policyOk = passwordStrengthOk(password);
  const policyIssues = React.useMemo(
    () => (password ? getPasswordPolicyIssues(password) : []),
    [password],
  );
  const issuesSummary = policyIssues.slice(0, 2).join(' ');
  const passwordLabels = React.useMemo(
    () => [
      t('signup.passwordStrength.veryWeak'),
      t('signup.passwordStrength.weak'),
      t('signup.passwordStrength.fair'),
      t('signup.passwordStrength.good'),
      t('signup.passwordStrength.strong'),
    ],
    [t],
  );
  const displayScore =
    passwordScore == null ? null : policyOk ? passwordScore : Math.min(passwordScore, 2);
  const strengthLabel =
    typeof displayScore === 'number'
      ? passwordLabels[Math.min(Math.max(displayScore, 0), passwordLabels.length - 1)]
      : null;
  const helperText = password
    ? policyOk
      ? t('signup.passwordPolicyOk')
      : issuesSummary || t('signup.passwordPolicyFail')
    : t('signup.passwordPolicyHint');

  const passwordMeter = (
    <div className="flex flex-col gap-1">
      <div className="flex gap-1">
        {Array.from({ length: 5 }).map((_, index) => {
          const active = displayScore != null && index <= displayScore;
          const color = active
            ? PASSWORD_COLORS[Math.min(displayScore ?? 0, PASSWORD_COLORS.length - 1)]
            : 'bg-neutral-200';
          return <span key={index} className={`h-2 flex-1 rounded ${color}`} />;
        })}
      </div>
      <div className="text-xs text-gray-600 dark:text-gray-300">
        {strengthLabel
          ? t('signup.passwordStrengthSummary', { strength: strengthLabel, helper: helperText })
          : helperText}
      </div>
      {passwordFeedback && (
        <div className="text-xs text-amber-600 dark:text-amber-400">{passwordFeedback}</div>
      )}
    </div>
  );

  const selectedLocaleLabel =
    availableLocales.find((entry) => entry.code === preferredLocale)?.label ||
    preferredLocale ||
    t('layout.language.english');
  const filteredLocales = React.useMemo(() => {
    if (!localeSearchDebounced) return availableLocales;
    return catalogLocales;
  }, [availableLocales, catalogLocales, localeSearchDebounced]);

  const installedLocaleCodes = React.useMemo(
    () => new Set(availableLocales.map((entry) => entry.code)),
    [availableLocales],
  );

  const handleLocaleSelect = async (nextLocale: string) => {
    const normalized = normalizeLocaleCode(nextLocale);
    if (!normalized) return;
    let resolved = normalized;
    if (!installedLocaleCodes.has(normalized)) {
      const installed = await installLocale(normalized);
      if (installed) {
        resolved = installed;
        try {
          const refreshed = await fetchInstalledLocales();
          setAvailableLocales(refreshed);
        } catch (err) {
          setLocalesError(
            err instanceof Error ? err.message : t('profile.language.loadFailed'),
          );
        }
      }
    }
    setPreferredLocale(resolved);
    setLocale(resolved);
    setLocaleDropdownOpen(false);
    setLocaleSearch('');
  };

  return (
    <>
      <Head>
        <title>{t('signup.documentTitle')}</title>
      </Head>
      <div className="min-h-screen flex flex-col items-center justify-center bg-neutral-100 dark:bg-neutral-900 py-12 px-4">
        <div className="w-full max-w-3xl bg-white dark:bg-neutral shadow-xl rounded-xl p-8">
          <h1 className="text-3xl font-semibold text-center text-primary dark:text-secondary mb-6">
            {t('signup.title')}
          </h1>

          {rolesLoading && (
            <p className="text-sm text-gray-500 dark:text-gray-300 mb-4">
              {t('signup.rolesLoading')}
            </p>
          )}
          {rolesError && <p className="text-sm text-red-600 mb-4">{rolesError}</p>}

          {submitted ? (
            <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg p-4">
              <h2 className="text-lg font-medium mb-2">{t('signup.receivedTitle')}</h2>
              <p className="text-sm">
                {t('signup.receivedDescription')}
              </p>
            </div>
          ) : (
            <form className="grid grid-cols-1 md:grid-cols-2 gap-4" onSubmit={onSubmit}>
              <div className="flex flex-col md:col-span-2" ref={localePickerRef}>
                <span className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t('signup.languageLabel')}
                </span>
                <button
                  type="button"
                  onClick={() => setLocaleDropdownOpen((prev) => !prev)}
                  className="flex items-center justify-between rounded border border-gray-300 bg-white p-3 text-left text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  aria-expanded={localeDropdownOpen}
                >
                  <span>{selectedLocaleLabel}</span>
                  <span className="text-xs text-gray-400">
                    {localesLoading ? t('common.state.loading') : t('profile.language.select')}
                  </span>
                </button>
                {localeDropdownOpen && (
                  <div className="relative">
                    <div className="absolute z-10 mt-1 w-full rounded border border-gray-200 bg-white p-2 shadow-lg">
                      <input
                        type="text"
                        value={localeSearch}
                        onChange={(event) => setLocaleSearch(event.target.value)}
                        placeholder={t('layout.language.searchPlaceholder')}
                        className="mb-2 w-full rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        autoFocus
                      />
                      <div className="max-h-48 overflow-y-auto">
                        {filteredLocales.length ? (
                          filteredLocales.map((entry) => (
                            <button
                              key={entry.code}
                              type="button"
                              onClick={() => handleLocaleSelect(entry.code)}
                              className={`flex w-full flex-col items-start rounded px-2 py-1 text-left text-sm transition hover:bg-gray-100 ${
                                entry.code === preferredLocale ? 'font-semibold text-blue-600' : ''
                              }`}
                            >
                              <span>{entry.label}</span>
                              <span className="text-xs text-gray-400">{entry.code}</span>
                            </button>
                          ))
                        ) : (
                          <div className="px-2 py-2 text-sm text-gray-500">
                            {localeSearchDebounced && catalogLoading
                              ? t('common.state.loading')
                              : localesError || catalogError
                                ? t('profile.language.loadFailed')
                                : t('common.state.noResults')}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              {inviteInfo?.invite_type === 'org' && inviteInfo.organization_id && (
                <div className="md:col-span-2 rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700">
                  {t('signup.inviteNotice')}
                </div>
              )}
              {inviteError && (
                <div className="md:col-span-2 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  {inviteError}
                </div>
              )}
              <label className="flex flex-col">
                <span className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t('profile.firstName')}
                </span>
                <input
                  type="text"
                  name="first_name"
                  value={firstName}
                  onChange={(event) => setFirstName(event.target.value)}
                  className="p-3 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary"
                  autoComplete="given-name"
                  placeholder={t('signup.firstNamePlaceholder')}
                  required
                />
              </label>
              <label className="flex flex-col">
                <span className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t('profile.lastName')}
                </span>
                <input
                  type="text"
                  name="last_name"
                  value={lastName}
                  onChange={(event) => setLastName(event.target.value)}
                  className="p-3 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary"
                  autoComplete="family-name"
                  placeholder={t('signup.lastNamePlaceholder')}
                  required
                />
              </label>
              <label className="flex flex-col md:col-span-2">
                <span className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t('profile.email')}
                </span>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="p-3 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary"
                  autoComplete="email"
                  placeholder={t('signup.emailPlaceholder')}
                  required
                />
              </label>
              <label className="flex flex-col md:col-span-2 gap-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t('signup.passwordLabel')}
                </span>
                <PasswordInput
                  name="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="p-3 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary"
                  autoComplete="new-password"
                  placeholder={t('signup.passwordPlaceholder')}
                  required
                />
                {passwordMeter}
              </label>
              <label className="flex flex-col md:col-span-2">
                <span className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t('signup.confirmPasswordLabel')}
                </span>
                <PasswordInput
                  name="confirm_password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  className="p-3 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary"
                  autoComplete="new-password"
                  placeholder={t('signup.confirmPasswordPlaceholder')}
                  required
                />
              </label>
              <fieldset className="flex flex-col gap-2 md:col-span-2">
                <legend className="text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t('signup.accountTypeLabel')}
                </legend>
                <div className="flex flex-col gap-2 md:flex-row md:items-center">
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                    <input
                      type="radio"
                      name="account_type"
                      value="individual"
                      checked={accountType === 'individual'}
                      onChange={() => {
                        setAccountType('individual');
                        setCompany('');
                      }}
                    />
                    {t('signup.accountTypeIndividual')}
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                    <input
                      type="radio"
                      name="account_type"
                      value="company"
                      checked={accountType === 'company'}
                      onChange={() => setAccountType('company')}
                    />
                    {t('signup.accountTypeCompany')}
                  </label>
                </div>
              </fieldset>
              {accountType === 'company' && (
                <label className="flex flex-col md:col-span-2">
                  <span className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">
                    {t('common.labels.company')}
                  </span>
                  <input
                    type="text"
                    name="company"
                    value={company}
                    onChange={(event) => setCompany(event.target.value)}
                    className="p-3 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder={t('signup.companyPlaceholder')}
                    required
                  />
                  <span className="text-xs text-gray-500 dark:text-gray-300">
                    {t('signup.companyHelper')}
                  </span>
                </label>
              )}
              <label className="flex flex-col md:col-span-2">
                <span className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t('common.labels.role')}
                </span>
                <select
                  name="role_key"
                  value={roleKey}
                  onChange={(event) => setRoleKey(event.target.value)}
                  className="p-3 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                  disabled={rolesLoading || rolesError != null}
                >
                  <option value="" disabled>
                    {rolesLoading ? t('common.state.loading') : t('signup.selectRole')}
                  </option>
                  {roles.map((role) => (
                    <option key={role.role_key} value={role.role_key}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col md:col-span-2">
                <span className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t('signup.referralCodeLabel')}
                </span>
                <input
                  type="text"
                  name="referral_code"
                  value={referralCode}
                  onChange={(event) => setReferralCode(event.target.value)}
                  className="p-3 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder={t('signup.referralCodePlaceholder')}
                />
              </label>
              <div className="md:col-span-2 flex flex-col gap-3">
                <div ref={captchaContainerRef} className="min-h-[65px]" aria-live="polite">
                  {!captchaSiteKey && (
                    <p className="text-xs text-gray-500 dark:text-gray-300">
                      {t('signup.captchaDisabled')}
                    </p>
                  )}
                  {captchaSiteKey && !captchaReady && (
                    <p className="text-xs text-gray-500 dark:text-gray-300">
                      {t('signup.captchaLoading')}
                    </p>
                  )}
                </div>
                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    name="acceptEula"
                    checked={acceptEula}
                    onChange={(event) => setAcceptEula(event.target.checked)}
                    className="mt-1"
                    required
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-200">
                    {t('signup.eulaAgreementPrefix')}{' '}
                    <a
                      href={EULA_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      {t('signup.eulaLink')}
                    </a>
                    {t('signup.eulaAgreementSuffix')}
                  </span>
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-300">
                  {t('signup.approvalNotice')}
                </p>
              </div>
              {error && (
                <div className="md:col-span-2 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">
                  {error}
                </div>
              )}
              <div className="md:col-span-2 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <Link href={`/login${marketingQuery}`} className="text-sm text-primary hover:underline">
                  {t('signup.alreadyHaveAccount')}
                </Link>
                <button
                  type="submit"
                  disabled={submitting || rolesLoading || !!rolesError}
                  className="bg-primary text-white px-6 py-3 rounded-md btn-hover btn-active disabled:opacity-70"
                >
                  {submitting ? t('signup.submitting') : t('signup.submit')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
